import * as React from "react";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import highChartsLangOptions from "../../../core/constants/highChartsLang";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import { makeStyles, Theme } from "@material-ui/core";
import get from "lodash/get";
import { merge } from "lodash";
import Heatmap from "highcharts/modules/heatmap";
import SolidGauge from "highcharts/modules/solid-gauge";
import { ChartCallbackFunction } from "highcharts";
import useConstructor from "../../../core/hooks/useConstructor";
import { EVENT_DARK_MODE } from "../../../Events";
import { LocalCache } from "../../../helpers/cache";
import { useHubXSubscribe } from "../../../HubX";
import { APP_WHITE, CHART_PRIMARY_COLOR } from "../../../RTUThemeProvider";

HighchartsMore(Highcharts);
Heatmap(Highcharts);
SolidGauge(Highcharts);
NoDataToDisplay(Highcharts);
Highcharts.setOptions({ ...highChartsLangOptions });

type styleProps = {
  maxHeight?: number;
};

const useStyles = makeStyles<Theme, styleProps>((theme: Theme) => ({
  chartContainer: ({ maxHeight }) => ({
    "height": "100%",
    "maxHeight": maxHeight || "unset",

    "& > *": {
      height: "100%",
    },
  }),
}));

type BaseChartProps = {
  options?: Highcharts.Options;
  constructorType?: "chart" | "stockChart" | "mapChart" | "ganttChart";
  allowChartUpdate?: boolean;
  maxHeight?: number;
  callback?: ChartCallbackFunction;
};

function BaseChart({
  options = {},
  maxHeight,
  constructorType = "chart",
  allowChartUpdate = true,
  callback,
}: BaseChartProps) {
  const ref = React.useRef(null);
  const classes = useStyles({ maxHeight });
  const [isDark, setIsDark] = React.useState(false);

  const CARD_BACKGROUND_ULTRA_DARK = "rgba(127, 255, 212, 0.75)";
  const CARD_BACKGROUND_DARK = "#424242";
  const CARD_BACKGROUND_LIGHT = "#f5f5f5";

  const lcache = new LocalCache();

  const getFromCache = () => {
    const prefersDark = lcache.get<boolean>("prefersDarkMode") || false;
    setIsDark(prefersDark);
  };

  useConstructor(async () => {
    getFromCache();
  });

  useHubXSubscribe(EVENT_DARK_MODE, (isDark: boolean) => {
    setIsDark(isDark);
  });

  const boxPlotConfig = React.useMemo(() => {
    return options?.chart?.type == "boxplot"
      ? isDark
        ? {
            colors: ["#fff", "#e2e"],
            plotOptions: {
              boxplot: {
                fillColor: CHART_PRIMARY_COLOR,
                medianColor: APP_WHITE,
                boxDashStyle: undefined,
                borderColor: "#000",
              },
            },
          }
        : {
            plotOptions: {
              boxplot: {
                fillColor: CHART_PRIMARY_COLOR,
                medianColor: APP_WHITE,
                boxDashStyle: undefined,
              },
            },
          }
      : {};
  }, [isDark, options]);

  const heatMapConfig = React.useMemo(() => {
    return options?.chart?.type == "heatmap"
      ? isDark
        ? {
            colors: ["#fff"],
            borderColor: "#000",
            colorAxis: {
              min: 0,
              minColor: "#B6BBBB",
              maxColor: CHART_PRIMARY_COLOR,
            },
          }
        : {
            colorAxis: {
              min: 0,
              minColor: "#FFFFFF",
              maxColor: CHART_PRIMARY_COLOR,
            },
          }
      : {};
  }, [isDark, options]);

  const theme = React.useMemo(() => {
    return isDark
      ? {
          xAxis: {
            labels: {
              style: {
                color: "#FFF",
              },
            },
          },
          yAxis: {
            labels: {
              style: {
                color: "#FFF",
              },
            },
          },
          colors: [
            CHART_PRIMARY_COLOR,
            "#fff",
            "#ED561B",
            "#DDDF00",
            "#24CBE5",
            "#64E572",
            "#FF9655",
            "#FFF263",
            "#6AF9C4",
          ],
          borderColor: "#000",
          chart: {
            backgroundColor: isDark ? CARD_BACKGROUND_DARK : CARD_BACKGROUND_LIGHT,
          },
          legend: {
            itemStyle: {
              color: "#fff",
            },
            itemHoverStyle: {
              color: "#fff",
            },
          },
          ...heatMapConfig,
          ...boxPlotConfig,
        }
      : {
          xAxis: {
            labels: {
              style: {
                color: "#666666",
              },
            },
          },
          yAxis: {
            labels: {
              style: {
                color: "#666666",
              },
            },
          },
          colors: [
            CHART_PRIMARY_COLOR,
            "#000",
            "#ED561B",
            "#DDDF00",
            "#24CBE5",
            "#64E572",
            "#FF9655",
            "#FFF263",
            "#6AF9C4",
          ],
          legend: {
            itemStyle: {
              color: "#424242",
            },
            itemHoverStyle: {
              color: "#424242",
            },
          },
          chart: {
            backgroundColor: "#fff",
          },
          ...heatMapConfig,
          ...boxPlotConfig,
        };
  }, [isDark]);

  // FIXME - corrigir tamanho dinâmico dos gráficos

  const baseOptions: Highcharts.Options = {
    ...options,
    chart: {
      zoomType: "x",
      spacingTop: options.title?.text ? undefined : 24, // fixes yAxis title on chart without title
      ...options.chart,
    },
    xAxis: {
      crosshair: true,
      ...options.xAxis,
    },
    credits: {
      enabled: false,
      ...options.credits,
    },
    tooltip: {
      outside: true,
      useHTML: true,
      ...options.tooltip,
    },
    time: {
      timezoneOffset: new Date().getTimezoneOffset(),
    },
  };

  React.useEffect(() => {
    const currentChart = get(ref, "current.chart");
    if (currentChart) {
      currentChart.reflow();
    }
  });

  const initialOptions = JSON.parse(JSON.stringify(Highcharts.getOptions()));

  const auxOptions: Highcharts.Options = React.useMemo(() => {
    const titleAux = {
      title: {
        style: {
          color: isDark ? "#424242" : "#fff",
        },
      },
    };
    const aux = merge(initialOptions, baseOptions, theme, titleAux);

    return aux;
  }, [theme, baseOptions, initialOptions, highChartsLangOptions]);

  return (
    <div className={classes.chartContainer}>
      <HighchartsReact
        ref={ref}
        options={auxOptions}
        constructorType={constructorType}
        allowChartUpdate={allowChartUpdate}
        highcharts={Highcharts}
        callback={callback}
      />
    </div>
  );
}

export default BaseChart;
